import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { AlertTriangle, ChevronDown, Eye } from "lucide-react";

const cell = "px-2 py-2 align-top";

export function BulkLegacyRow({ row, index, onChange }) {
    if (row.error) {
        return (
            <tr className="border-t border-border" data-testid={`bulk-legacy-row-${index}`}>
                <td className={cell}>{row.filename}</td>
                <td className={`${cell} text-red-600`} colSpan={7}>{row.error}</td>
            </tr>
        );
    }
    return (
        <tr className="border-t border-border hover:bg-secondary/20" data-testid={`bulk-legacy-row-${index}`}>
            <td className={`${cell} max-w-[180px]`}>
                <button onClick={() => viewPdf(row.staging_id)} className="flex items-center gap-1 text-primary hover:underline truncate max-w-full"
                        data-testid={`bulk-legacy-view-${index}`} title={row.filename}>
                    <Eye className="w-3 h-3 shrink-0" /><span className="truncate">{row.filename}</span>
                </button>
                {!row.has_text && <div className="text-[10px] text-amber-600 mt-0.5">No readable text — check fields</div>}
                {row.duplicate_of && (
                    <div className="text-[10px] text-amber-600 mt-0.5 flex items-center gap-1" data-testid={`bulk-legacy-dup-${index}`}>
                        <AlertTriangle className="w-3 h-3" />Duplicate of {row.duplicate_of.invoice_number}
                    </div>
                )}
            </td>
            <td className={cell}>
                <CustomerPicker index={index} customer={row.customer} matchedOn={row.matched_on}
                                onPick={(c) => onChange({ customer: c, matched_on: "manual", duplicate_of: null })} />
            </td>
            <td className={cell}>
                <Input type="number" step="0.01" value={row.amount} className="h-8 w-24 text-xs"
                       onChange={(e) => onChange({ amount: e.target.value, amount_source: null })}
                       data-testid={`bulk-legacy-amount-${index}`} />
                {row.amount_source === "customer_rate" && <div className="text-[10px] text-muted-foreground mt-0.5">from customer rate</div>}
            </td>
            <td className={cell}>
                <Input type="date" value={row.invoice_date} className="h-8 w-36 text-xs"
                       onChange={(e) => onChange({ invoice_date: e.target.value })}
                       data-testid={`bulk-legacy-date-${index}`} />
            </td>
            <td className={cell}>
                <Input type="date" value={row.due_date} className="h-8 w-36 text-xs"
                       onChange={(e) => onChange({ due_date: e.target.value })}
                       data-testid={`bulk-legacy-due-${index}`} />
            </td>
            <td className={cell}>
                <Input value={row.legacy_invoice_number} className="h-8 w-24 text-xs"
                       onChange={(e) => onChange({ legacy_invoice_number: e.target.value })}
                       data-testid={`bulk-legacy-number-${index}`} />
            </td>
            <td className={cell}>
                <Select value={row.status} onValueChange={(v) => onChange({ status: v })}>
                    <SelectTrigger className="h-8 w-24 text-xs" data-testid={`bulk-legacy-status-${index}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="paid">Paid</SelectItem>
                    </SelectContent>
                </Select>
            </td>
            <td className={cell}>
                <Select value={row.action} onValueChange={(v) => onChange({ action: v })}>
                    <SelectTrigger className="h-8 w-24 text-xs" data-testid={`bulk-legacy-action-${index}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="create">Create</SelectItem>
                        <SelectItem value="skip">Skip</SelectItem>
                    </SelectContent>
                </Select>
            </td>
        </tr>
    );
}

function CustomerPicker({ index, customer, matchedOn, onPick }) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);

    useEffect(() => {
        if (!open) return;
        const t = setTimeout(async () => {
            const { data } = await api.get("/customers", { params: q ? { q, limit: 15 } : { limit: 15 } });
            setResults(data || []);
        }, 200);
        return () => clearTimeout(t);
    }, [q, open]);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button className={`text-left w-44 rounded border px-2 py-1 hover:bg-secondary/40 transition-colors ${customer ? "border-border" : "border-red-300 bg-red-50/50 dark:bg-red-950/20"}`}
                        data-testid={`bulk-legacy-customer-${index}`}>
                    <div className="flex items-center justify-between gap-1">
                        <span className="truncate font-medium">{customer?.name || "Pick customer"}</span>
                        <ChevronDown className="w-3 h-3 shrink-0" />
                    </div>
                    {customer && (
                        <div className="text-[10px] text-muted-foreground truncate">
                            {customer.account_number} · {(matchedOn || "").replaceAll("_", " ")}
                        </div>
                    )}
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-2" align="start">
                <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customers..."
                       className="h-8 text-xs mb-2" data-testid={`bulk-legacy-customer-search-${index}`} />
                <div className="max-h-60 overflow-y-auto divide-y">
                    {results.map((c) => (
                        <button key={c.id} className="w-full text-left px-2 py-1.5 hover:bg-secondary/50 text-xs"
                                onClick={() => { onPick({ id: c.id, name: c.name, account_number: c.account_number }); setOpen(false); }}
                                data-testid={`bulk-legacy-pick-${index}-${c.id}`}>
                            <div className="font-medium">{c.name}</div>
                            <div className="text-[10px] text-muted-foreground">{c.account_number} · {c.address?.line1}</div>
                        </button>
                    ))}
                    {!results.length && <div className="text-xs text-muted-foreground p-2">No customers</div>}
                </div>
            </PopoverContent>
        </Popover>
    );
}

async function viewPdf(fileId) {
    try {
        const { data } = await api.get(`/uploads/${fileId}`, { responseType: "blob" });
        window.open(URL.createObjectURL(new Blob([data], { type: "application/pdf" })), "_blank");
    } catch {
        toast.error("Could not open PDF");
    }
}
